import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useSensorData } from '@/hooks/useSensorData';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { measurePerformance } from '@/utils/performance';
import { calculateTargetRangeAxis } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useEffect, useMemo, useRef } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { DEVICE_COLORS, CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import type { ChartAnnotation } from '@/types/chartAnnotation';

interface SalinityChartProps {
  startDate: Date;
  endDate: Date;
  /** Pre-fetched sensor data from consolidated dashboard endpoint */
  sensorData?: Array<{ timestamp: string; salinity: number | null }>;
  /** Pre-fetched manual salinity water test data */
  manualData?: Array<{ timestamp: string; value: number; device: string; note?: string }>;
  /** Pre-fetched target ranges for axis scaling */
  targetRangesData?: Array<{ parameterId: string; minValue: number | null; maxValue: number | null; unit: string }>;
  /** Downsampling info from consolidated endpoint */
  interval?: string;
  isDownsampled?: boolean;
  /** True when moving average is applied - sensor data uses timestamps directly */
  isAveraged?: boolean;
  /** Loading state from parent */
  isLoading?: boolean;
  /** Error from parent */
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
  /** Annotations to display on the chart */
  annotations?: ChartAnnotation[];
  /** Callback when the "Add note" button is clicked */
  onAddNote?: () => void;
  /** Callback when an annotation is clicked for editing */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

interface ChartDataPoint {
  timestamp: number;
  salinity?: number | null;
  manual?: number;
  [key: `${string}_note`]: string | undefined;
}

export function SalinityChart({
  startDate,
  endDate,
  sensorData,
  manualData: parentManualData,
  targetRangesData,
  interval: parentInterval,
  isDownsampled: parentDownsampled,
  isAveraged: _isAveraged = false,  // Reserved for future use when grouping is needed
  isLoading: parentLoading,
  error: parentError,
  expanded,
  onToggle,
  annotations,
  onAddNote,
  onAnnotationClick,
}: SalinityChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Responsive hooks
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Use hook as fallback when props not provided
  // Disable the hook query when parent provides data OR parent is loading
  const hookResult = useSensorData({
    startDate,
    endDate,
    parameters: ['salinity'],
    enabled: sensorData === undefined && parentLoading !== true,
  });

  // Use parent data if provided, otherwise use hook data
  const rawData = sensorData ?? hookResult.data?.data;
  const rawManualData = parentManualData;
  const isLoading = parentLoading ?? hookResult.isLoading;
  const error = parentError ?? hookResult.error;
  const isDownsampled = parentDownsampled ?? hookResult.data?.downsampled ?? false;
  const interval = parentInterval ?? hookResult.data?.interval;

  // Convert string timestamps to numbers and merge sensor + manual data
  const chartData = useMemo(() => {
    if (!rawData && !rawManualData) return [];

    const grouped = new Map<number, ChartDataPoint>();

    // Add sensor data
    if (rawData) {
      rawData.forEach((point) => {
        const timestamp = new Date(point.timestamp).getTime();
        if (!grouped.has(timestamp)) {
          grouped.set(timestamp, { timestamp });
        }
        const entry = grouped.get(timestamp);
        if (entry && point.salinity != null) {
          entry.salinity = point.salinity;
        }
      });
    }

    // Add manual salinity data
    if (rawManualData) {
      rawManualData.forEach((point) => {
        const timestamp = new Date(point.timestamp).getTime();
        if (!grouped.has(timestamp)) {
          grouped.set(timestamp, { timestamp });
        }
        const entry = grouped.get(timestamp);
        if (entry) {
          entry.manual = point.value;
          if (point.note) entry.manual_note = point.note;
        }
      });
    }

    return Array.from(grouped.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [rawData, rawManualData]);

  // Range-aware X-axis: dates for multi-day, times for single-day
  const timeAxis = useTimeAxis(startDate, endDate);

  // Find salinity target range from user config or global defaults
  const salRange = useMemo(() => {
    return targetRangesData?.find(r => r.parameterId === 'salinity');
  }, [targetRangesData]);

  // Calculate Y-axis from target ranges + data (no hardcoded axis bounds)
  const yAxisConfig = useMemo(() => {
    const values: number[] = [];
    chartData.forEach((d) => {
      if (d.salinity != null && typeof d.salinity === 'number' && !isNaN(d.salinity)) {
        values.push(d.salinity);
      }
      if (d.manual != null && typeof d.manual === 'number' && !isNaN(d.manual)) {
        values.push(d.manual);
      }
    });

    const hasValues = values.length > 0;

    return calculateTargetRangeAxis({
      dataMin: hasValues ? Math.min(...values) : undefined,
      dataMax: hasValues ? Math.max(...values) : undefined,
      targetMin: salRange?.minValue ?? undefined,
      targetMax: salRange?.maxValue ?? undefined,
    });
  }, [chartData, salRange]);

  // Measure chart render performance
  useEffect(() => {
    if (rawData && !isLoading) {
      const measure = measurePerformance('SalinityChart');
      const timerId = setTimeout(() => measure.end(), 0);
      return () => clearTimeout(timerId);
    }
  }, [rawData, isLoading]);

  // Detect which data sources have data for conditional legend rendering
  const hasSalinity = chartData.some((d) => d.salinity != null);
  const hasManual = chartData.some((d) => d.manual != null);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <ChartSkeleton title="Salinity" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading salinity data">
        {error.message}
      </Alert>
    );
  }

  const hasData = chartData.length > 0 && yAxisConfig !== null;

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="Salinity"
        subtitle={isMobile ? '(ppt)' : (isDownsampled ? `(Downsampled to ${interval})` : undefined)}
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
        onAddNote={onAddNote}
      />

      {!hasData && !isLoading && (
        <Alert color="blue" title="No data available">
          No salinity data found for the selected date range.
        </Alert>
      )}

      {hasData && (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          {/* Annotation regions - rendered behind data lines */}
          {annotations && (
            <ChartAnnotationLayer
              annotations={filterAnnotationsForChart(
                annotations,
                'salinity',
                ['apex', 'manual']
              )}
              onAnnotationClick={onAnnotationClick}
            />
          )}

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          {/* Crosshair tooltip with custom content */}
          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => `${v.toFixed(1)} ppt`}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {/* Main salinity line - only show if has data */}
          {hasSalinity && (
            <Line
              type="linear"
              dataKey="salinity"
              stroke={DEVICE_COLORS.sensor}
              strokeWidth={1.5}
              dot={false}
              name="Salinity"
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          )}

          {/* Manual salinity entries - only show if has data */}
          {hasManual && (
            <Line
              type="monotone"
              dataKey="manual"
              stroke={DEVICE_COLORS.manual}
              strokeWidth={1.5}
              dot={{ r: 4, fill: DEVICE_COLORS.manual, strokeWidth: 0 }}
              name="Manual"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      )}
    </Paper>
  );
}
